import { useMutation } from '@apollo/client';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import Image from 'next/image';
import Link from 'next/link';
import { useState } from 'react';

import {
  FORGOT_PASSWORD_MUTATION,
  ForgotPasswordResponse,
} from 'api/session/sign-up';

import { IStatusResultEnum } from 'utils/enums/status';
import {
  getGraphqlErrors,
  isGraphqlError,
} from 'utils/functions/isGraphqlError';
import { ForgotPasswordValidationSchema } from 'utils/functions/validationSchemas';

import Dots from 'assets/icons/dots.svg';
import SemiCircleDots from 'assets/icons/semicircle-dots.svg';
import SemiTriangleDots from 'assets/icons/semitriangle-dots.svg';
import Square from 'assets/icons/square.svg';

const ForgotPassword = () => {
  const [acceptForgotPassword, { loading: loadingAcceptForgotPassword }] =
    useMutation<{
      startForgotPassword: ForgotPasswordResponse;
    }>(FORGOT_PASSWORD_MUTATION);

  const [forgotPasswordResponse, setForgotPasswordResponse] = useState(null);

  const startForgotPassword = async values => {
    try {
      const response = await acceptForgotPassword({
        variables: {
          input: {
            email: values.email,
          },
        },
      });

      if (
        response.data.startForgotPassword.status ===
        IStatusResultEnum.Processing
      ) {
        setForgotPasswordResponse({
          error: false,
          message: response.data.startForgotPassword.message,
        });
      }
    } catch (error) {
      if (isGraphqlError(error)) {
        const errorMessage = getGraphqlErrors(error);
        setForgotPasswordResponse({
          error: true,
          message: errorMessage,
        });
      }
    }
  };

  return (
    <div className="w-screen h-screen flex flex-row ">
      <div className="h-full w-7/12 flex flex-col bg-login">
        <div className="mt-9 ml-12 mb-44">
          <Image
            src="/img/endless-logo.svg"
            width={170}
            height={25}
            alt="Endless logo"
          />
        </div>
        <Dots className="w-20 h-5 absolute left-24 top-40" />
        <Square className="w-7 h-7 absolute left-1/2 top-44" />
        <h1 className="ml-28 mb-6 text-7xl font-aeonik-medium">
          Simplify ops. Supercharge growth.
        </h1>
        <h3 className="ml-28 text-lg">
          A single dashboard to connect, manage, and scale your operations.
        </h3>
        <SemiCircleDots className="absolute left-1/2 bottom-52" />
        <Square className="w-7 h-7 absolute left-28 bottom-28" />
        <Dots className="w-20 h-5 absolute left-1/3 bottom-20" />
        <SemiTriangleDots className="absolute left-48 bottom-0" />
      </div>

      <div className="flex flex-col h-full w-5/12 z-10 bg-neutral-10 items-stretch justify-items-auto shadow-login">
        <div className="mx-20 mt-40 mb-16">
          <h2 className="text-3xl font-aeonik-medium mb-2">
            Reset your password
          </h2>
          <h3 className="text-lg">
            We&apos;ll send you instructions to reset your password and get you
            back on track.
          </h3>
        </div>
        <Formik
          initialValues={{
            email: '',
          }}
          validationSchema={ForgotPasswordValidationSchema}
          onSubmit={startForgotPassword}
          enableReinitialize
        >
          {({ isValid, errors, touched }) => (
            <Form className="flex flex-col mx-20">
              {!forgotPasswordResponse && (
                <>
                  <div className="flex flex-col mb-6 gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="aeonik text-md text-gray-500">
                        Email
                      </label>
                      <Field
                        name="email"
                        type="text"
                        placeholder="name@email.com"
                        className={`border w-full h-12 text-sm focus:outline-none mr-1 rounded-lg bg-surface text-neutrals-80 placeholder:text-neutrals-60 ${
                          errors.email
                            ? 'border-danger-40 text-danger-60'
                            : 'border-neutrals-40'
                        }`}
                      />
                      <ErrorMessage
                        name="password"
                        component="span"
                        className="block text-xs text-danger-40"
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={
                      loadingAcceptForgotPassword ||
                      !isValid ||
                      Object.keys(touched).length === 0
                    }
                    className={`border h-12 text-sm focus:outline-none mr-1 rounded-full ${
                      isValid && Object.keys(touched).length > 0
                        ? 'bg-primary-60 text-white'
                        : 'bg-neutrals-40 text-neutrals-100'
                    }`}
                  >
                    FORGOT PASSWORD
                  </button>
                </>
              )}

              {forgotPasswordResponse && (
                <>
                  <label
                    htmlFor="email"
                    className="aeonik mb-5 text-xl font-aeonik-medium"
                  >
                    {!forgotPasswordResponse.error
                      ? '🎉 Check your email'
                      : '❌ Error sending email'}
                  </label>

                  <p className="aeonik text-md text-gray-500">
                    {forgotPasswordResponse.error
                      ? 'An error has occurred during the process. Please try again later.'
                      : forgotPasswordResponse.message}
                  </p>

                  <Link href="/login">
                    <button
                      type="button"
                      className="h-12 mt-8 text-sm focus:outline-none mr-1 px-5 rounded-full bg-primary-60 text-white"
                    >
                      RETURN TO LOGIN
                    </button>
                  </Link>
                </>
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default ForgotPassword;
